import React, { useState, useEffect } from 'react';
import { X, ZoomIn, ZoomOut, RotateCw, Download, ChevronLeft, ChevronRight, Camera, FileSignature } from 'lucide-react';
import { Task } from '../../types';

interface PhotoSignatureModalProps {
  task: Task;
  photos?: string[];
  signature?: string;
  onClose: () => void;
}

const PhotoSignatureModal: React.FC<PhotoSignatureModalProps> = ({
  task,
  photos = [],
  signature,
  onClose
}) => {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loadingStates, setLoadingStates] = useState<{ [key: string]: boolean }>({});
  const [errorStates, setErrorStates] = useState<{ [key: string]: boolean }>({});
  const [viewMode, setViewMode] = useState<'photos' | 'signature'>('photos');

  const hasPhotos = photos && photos.length > 0;
  const hasSignature = !!signature;
  const currentPhoto = hasPhotos ? photos[currentPhotoIndex] : null;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isFullscreen) {
          setIsFullscreen(false);
        } else {
          onClose();
        }
      } else if (e.key === 'ArrowLeft' && hasPhotos && currentPhotoIndex > 0) {
        handlePreviousPhoto();
      } else if (e.key === 'ArrowRight' && hasPhotos && currentPhotoIndex < photos.length - 1) {
        handleNextPhoto();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen, currentPhotoIndex, photos, hasPhotos]);

  useEffect(() => {
    if (!hasPhotos && hasSignature) {
      setViewMode('signature');
    }
  }, [hasPhotos, hasSignature]);

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleRotateRight = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  const handleRotateLeft = () => {
    setRotation(prev => (prev - 90 + 360) % 360);
  };

  const handleDownload = async (url: string, filename: string) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading image:', error);
      alert('Erreur lors du téléchargement de l\'image');
    }
  };

  const handlePreviousPhoto = () => {
    if (currentPhotoIndex > 0) {
      setCurrentPhotoIndex(prev => prev - 1);
      resetViewSettings();
    }
  };

  const handleNextPhoto = () => {
    if (currentPhotoIndex < photos.length - 1) {
      setCurrentPhotoIndex(prev => prev + 1);
      resetViewSettings();
    }
  };

  const resetViewSettings = () => {
    setZoom(1);
    setRotation(0);
  };

  const handleImageLoad = (url: string) => {
    setLoadingStates(prev => ({ ...prev, [url]: false }));
    setErrorStates(prev => ({ ...prev, [url]: false }));
  };

  const handleImageError = (url: string) => {
    setLoadingStates(prev => ({ ...prev, [url]: false }));
    setErrorStates(prev => ({ ...prev, [url]: true }));
  };

  const renderImageViewer = (imageUrl: string, title: string) => {
    const isLoading = loadingStates[imageUrl];
    const hasError = errorStates[imageUrl];

    return (
      <div className="relative bg-gray-100 rounded-lg overflow-hidden" style={{ minHeight: '400px' }}>
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        )}

        {hasError ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500">
            <Camera className="h-16 w-16 mb-4" />
            <p>Erreur de chargement de l'image</p>
          </div>
        ) : (
          <>
            <div
              className={`flex items-center justify-center p-4 ${isFullscreen ? 'fixed inset-0 bg-black z-50' : ''}`}
              onClick={() => !isFullscreen && setIsFullscreen(true)}
            >
              <img
                src={imageUrl}
                alt={title}
                onLoad={() => handleImageLoad(imageUrl)}
                onError={() => handleImageError(imageUrl)}
                style={{
                  transform: `scale(${zoom}) rotate(${rotation}deg)`,
                  transition: 'transform 0.3s ease',
                  maxWidth: isFullscreen ? '90vw' : '100%',
                  maxHeight: isFullscreen ? '90vh' : '500px',
                  cursor: isFullscreen ? 'zoom-out' : 'zoom-in',
                  objectFit: 'contain'
                }}
                className="select-none"
              />
            </div>

            {isFullscreen && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsFullscreen(false);
                }}
                className="fixed top-4 right-4 z-50 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100"
              >
                <X className="h-6 w-6" />
              </button>
            )}

            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white rounded-lg shadow-lg p-2 flex items-center space-x-2">
              <button
                onClick={handleZoomOut}
                disabled={zoom <= 0.5}
                className="p-2 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                title="Zoom arrière"
              >
                <ZoomOut className="h-5 w-5" />
              </button>

              <span className="text-sm font-medium px-2 min-w-[60px] text-center">
                {Math.round(zoom * 100)}%
              </span>

              <button
                onClick={handleZoomIn}
                disabled={zoom >= 3}
                className="p-2 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                title="Zoom avant"
              >
                <ZoomIn className="h-5 w-5" />
              </button>

              <div className="w-px h-6 bg-gray-300 mx-1"></div>

              <button
                onClick={handleRotateLeft}
                className="p-2 hover:bg-gray-100 rounded"
                title="Rotation à gauche"
              >
                <RotateCw className="h-5 w-5 transform scale-x-[-1]" />
              </button>

              <button
                onClick={handleRotateRight}
                className="p-2 hover:bg-gray-100 rounded"
                title="Rotation à droite"
              >
                <RotateCw className="h-5 w-5" />
              </button>

              <div className="w-px h-6 bg-gray-300 mx-1"></div>

              <button
                onClick={() => handleDownload(imageUrl, title)}
                className="p-2 hover:bg-gray-100 rounded"
                title="Télécharger"
              >
                <Download className="h-5 w-5" />
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  if (!hasPhotos && !hasSignature) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 z-10">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold">Photos et Signature</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {hasPhotos && hasSignature && (
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  setViewMode('photos');
                  resetViewSettings();
                }}
                className={`flex items-center px-4 py-2 rounded-lg ${
                  viewMode === 'photos'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Camera className="h-4 w-4 mr-2" />
                Photos ({photos.length})
              </button>
              <button
                onClick={() => {
                  setViewMode('signature');
                  resetViewSettings();
                }}
                className={`flex items-center px-4 py-2 rounded-lg ${
                  viewMode === 'signature'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <FileSignature className="h-4 w-4 mr-2" />
                Signature
              </button>
            </div>
          )}
        </div>

        <div className="p-6">
          {viewMode === 'photos' && hasPhotos && (
            <div>
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold flex items-center">
                  <Camera className="h-5 w-5 mr-2" />
                  Photos ({currentPhotoIndex + 1}/{photos.length})
                </h3>

                {photos.length > 1 && (
                  <div className="flex space-x-2">
                    <button
                      onClick={handlePreviousPhoto}
                      disabled={currentPhotoIndex === 0}
                      className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Photo précédente"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                      onClick={handleNextPhoto}
                      disabled={currentPhotoIndex === photos.length - 1}
                      className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Photo suivante"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </div>
                )}
              </div>

              {currentPhoto && renderImageViewer(currentPhoto, `photo-${currentPhotoIndex + 1}.jpg`)}

              {photos.length > 1 && (
                <div className="mt-4 flex space-x-2 overflow-x-auto">
                  {photos.map((photo, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setCurrentPhotoIndex(index);
                        resetViewSettings();
                      }}
                      className={`flex-shrink-0 border-2 rounded-lg overflow-hidden ${
                        index === currentPhotoIndex
                          ? 'border-blue-600'
                          : 'border-gray-300 opacity-60 hover:opacity-100'
                      }`}
                      style={{ width: '100px', height: '100px' }}
                    >
                      <img
                        src={photo}
                        alt={`Miniature ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {viewMode === 'signature' && hasSignature && (
            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <FileSignature className="h-5 w-5 mr-2" />
                Signature
              </h3>
              {renderImageViewer(signature, 'signature.jpg')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhotoSignatureModal;
